import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, FolderPlus } from 'lucide-react'
import { getProjects, getDocuments, getDocument, updateDocument } from '../../services/api'
import type { PatentBrowseResult } from '../../services/api'
import { disambiguateProjectLabels } from '../../utils/projects'
import type { PatentReference } from '../../types'

interface AddPatentToProjectMenuProps {
  patent: PatentBrowseResult
}

// PatentReference.jurisdiction is a closed union — real patent offices (WO, JP,
// CN, ...) don't fit it, so map to the nearest real value instead of dropping
// the field or inventing a match.
function toPatentReferenceJurisdiction(jurisdiction: string): PatentReference['jurisdiction'] {
  const upper = (jurisdiction || '').toUpperCase()
  if (upper === 'AU' || upper === 'US' || upper === 'EP') return upper
  if (upper === 'PCT' || upper === 'WO') return 'PCT'
  return 'OTHER'
}

/**
 * "Add to project" for a searched/browsed patent = declare it as prior art on
 * one of the project's documents (Document.declared_patents) — the same field
 * and PUT /documents/{id} flow DocumentDetail.tsx already uses for manually
 * declared patents. There is no generic patent-attach endpoint (folder-lists
 * only accepts resource_type journal/contact/organisation), so this reuses the
 * real, existing declared_patents mechanism via a project -> document picker
 * instead of inventing a write path.
 */
export default function AddPatentToProjectMenu({ patent }: AddPatentToProjectMenuProps) {
  const [open, setOpen] = useState(false)
  const [projectId, setProjectId] = useState('')
  const [documentId, setDocumentId] = useState('')
  const [saving, setSaving] = useState(false)
  const [added, setAdded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    enabled: open,
  })

  const { data: documents, isLoading: documentsLoading } = useQuery({
    queryKey: ['documents', projectId],
    queryFn: () => getDocuments(projectId),
    enabled: open && !!projectId,
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleAdd = async () => {
    if (!documentId) return
    setSaving(true)
    setError(null)
    try {
      // Read-then-write: declared_patents is a full-array field on the
      // document, so we must merge against its current value, not the
      // (possibly stale) list-endpoint copy, to avoid clobbering entries
      // added since the document list was fetched.
      const doc = await getDocument(documentId)
      const next: PatentReference[] = [
        ...(doc.declared_patents ?? []),
        {
          number: patent.patent_id,
          jurisdiction: toPatentReferenceJurisdiction(patent.jurisdiction),
          filed_date: patent.filing_date || null,
        },
      ]
      await updateDocument(documentId, { declared_patents: next })
      setAdded(true)
      setTimeout(() => {
        setAdded(false)
        setOpen(false)
      }, 1200)
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setError(e?.response?.data?.error || e?.message || 'Failed to add to project')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation()
          setOpen(!open)
        }}
        className="p-1.5 text-gray-500 hover:text-iflow-600 flex-shrink-0"
        title="Add to project"
      >
        <FolderPlus size={14} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-3"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xs text-gray-500 mb-2">Declare as prior art on a document.</p>
          <label className="block text-[10px] font-medium text-gray-500 mb-1">Project</label>
          <select
            data-testid="add-to-project-project-select"
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value)
              setDocumentId('')
              setError(null)
            }}
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs mb-2"
          >
            <option value="">Select a project...</option>
            {projects
              ? disambiguateProjectLabels(projects).map((p) => (
                  <option key={p.project_id} value={p.project_id}>{p.displayName}</option>
                ))
              : null}
          </select>

          <label className="block text-[10px] font-medium text-gray-500 mb-1">Document</label>
          <select
            data-testid="add-to-project-document-select"
            value={documentId}
            onChange={(e) => {
              setDocumentId(e.target.value)
              setError(null)
            }}
            disabled={!projectId || documentsLoading}
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs mb-2 disabled:bg-gray-50 disabled:text-gray-500"
          >
            <option value="">
              {!projectId
                ? 'Select a project first...'
                : documentsLoading
                  ? 'Loading documents...'
                  : (documents && documents.length > 0)
                    ? 'Select a document...'
                    : 'No documents in this project'}
            </option>
            {(documents || []).map((d) => (
              <option key={d.document_id} value={d.document_id}>{d.title || d.filename || d.document_id}</option>
            ))}
          </select>

          {error && <p data-testid="add-to-project-error" className="text-xs text-red-600 mb-2">{error}</p>}

          <button
            data-testid="add-to-project-confirm"
            onClick={handleAdd}
            disabled={!documentId || saving}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-iflow-600 rounded-lg hover:bg-iflow-700 disabled:opacity-50"
          >
            {added ? (
              <>
                <Check size={12} /> Added
              </>
            ) : saving ? (
              'Adding...'
            ) : (
              'Add to project'
            )}
          </button>
        </div>
      )}
    </div>
  )
}
